import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { LuBell, LuLogOut, LuUser } from 'react-icons/lu';
import { logoutAndRedirect } from '../../auth/session';
import { urlApiProd } from '../../vistas/utiles';

const money = (value) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: 'BRL', maximumFractionDigits: 2,
}).format(Number(value) || 0);
const paymentStatusLabel = (status) => status === 'Vencida' ? 'Vencido' : status;

export default function Navigation() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const alertsRef = useRef(null);

  const goToPaymentControl = (search = '') => {
    setShowAlerts(false);
    navigate({ pathname: '/finanzas/proveedores', search });
  };

  const loadAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${urlApiProd}/finanzas/proveedores/alertas`);
      if (!response.ok) return;
      const body = await response.json();
      setAlerts(body.alertas || []);
    } catch {
      // La barra superior no debe interrumpir la navegación si falla una alerta.
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    const timer = setInterval(loadAlerts, 60000);
    window.addEventListener('focus', loadAlerts);
    window.addEventListener('finanzas-alertas-updated', loadAlerts);
    return () => { clearInterval(timer); window.removeEventListener('focus', loadAlerts); window.removeEventListener('finanzas-alertas-updated', loadAlerts); };
  }, [loadAlerts]);

  useEffect(() => {
    if (!showAlerts) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!alertsRef.current?.contains(event.target)) setShowAlerts(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowAlerts(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showAlerts]);

  const logout = async () => {
    setIsLoggingOut(true);
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await fetch(`${urlApiProd}/logout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // El cierre local se completa aunque no responda el servidor.
      }
    }
    logoutAndRedirect('logout');
  };

  const total = alerts.reduce((sum, row) => sum + Number(row.cantidad_nf || 0), 0);
  return (
    <>
      <header className="erp-topbar">
        <span className="erp-topbar-title">Palm Trade Chile</span>
        <div className="erp-topbar-actions">
          <div className="erp-user"><LuUser /><span>{localStorage.getItem('Nombre') || 'Usuario'}</span></div>
          <div className="erp-alerts-trigger" ref={alertsRef}>
            <button type="button" className="erp-topbar-icon" title="Alertas de pago" aria-label={`Alertas de pago: ${total}`} aria-expanded={showAlerts} onClick={() => setShowAlerts((visible) => !visible)}>
              <LuBell />{total > 0 && <span className="erp-alert-count">{total > 99 ? '99+' : total}</span>}
            </button>
            {showAlerts && (
              <section className="erp-alerts-popover" aria-label="Alertas de pago a proveedores">
                <div className="erp-alerts-heading">
                  <strong>Alertas de pago</strong>
                  <span>{total} NF requieren gestión</span>
                </div>
                <div className="erp-alerts-list">
                  {alerts.map((row) => (
                    <button key={`${row.id_proveedor}-${row.estado_pago}`} type="button" onClick={() => goToPaymentControl(`?proveedor=${row.id_proveedor}&estado=${encodeURIComponent(row.estado_pago)}`)}>
                      <span><strong>{row.proveedor}</strong><small>{paymentStatusLabel(row.estado_pago)}</small></span>
                      <span><strong>{row.cantidad_nf} NF</strong><small>{money(row.monto_brl)}</small></span>
                    </button>
                  ))}
                  {!alerts.length && <p>No hay NF vencidas ni solicitudes pendientes de gestión.</p>}
                </div>
                <button className="erp-alerts-footer" type="button" onClick={() => goToPaymentControl()}>Ir al control de pagos</button>
              </section>
            )}
          </div>
          <button type="button" className="erp-topbar-icon" title="Cerrar sesión" aria-label="Cerrar sesión" onClick={() => setShowLogoutConfirm(true)}><LuLogOut /></button>
        </div>
      </header>
      <Modal show={showLogoutConfirm} onHide={() => !isLoggingOut && setShowLogoutConfirm(false)} centered>
        <Modal.Header closeButton={!isLoggingOut}>
          <Modal.Title>Confirmar cierre de sesión</Modal.Title>
        </Modal.Header>
        <Modal.Body>¿Realmente quieres cerrar sesión?</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" disabled={isLoggingOut} onClick={() => setShowLogoutConfirm(false)}>Cancelar</button>
          <button type="button" className="btn erp-logout-confirm" disabled={isLoggingOut} onClick={logout}>{isLoggingOut ? 'Cerrando...' : 'Cerrar sesión'}</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
